import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { cssVars } from '../../utilities/css-vars'
import styles from './SkeletonLoader.module.scss'

type ChildrenRenderer<T> = (props: T) => React.ReactNode

type ChildrenProps<T> = React.ReactNode | ChildrenRenderer<T>

function renderChildren<T>(children: ChildrenProps<T>, props: T): React.ReactNode {
  return typeof children === 'function' ? (children as ChildrenRenderer<T>)(props) : children
}

export type SkeletonAttributes = {
  minWidth: number
  minHeight: number
  hidden?: boolean
}

interface SkeletonLoaderProps {
  isLoading: boolean
  hidden?: boolean
  findTag?:
    | 'BUTTON'
    | 'DIV'
    | 'SPAN'
    | 'P'
    | 'H1'
    | 'H2'
    | 'H3'
    | 'H4'
    | 'H5'
    | 'H6'
    | 'IMG'
    | 'A'
    | 'TD'
    | 'PICTURE'
  children: ChildrenProps<{
    skeletonAttributes: ({ minWidth, minHeight }: SkeletonAttributes) => {
      'data-skeleton-load': string
    }
  }>
}

export const SkeletonLoader = ({
  isLoading,
  findTag,
  children,
  hidden = false,
}: SkeletonLoaderProps) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)
  const [dimensions, setDimensions] = useState<{
    width: number
    height: number
    borderRadius: string
    className: string
  }>({ width: 0, height: 0, borderRadius: '4px', className: '' })

  const skeletonAttributes = (props: SkeletonAttributes) => ({
    'data-skeleton-load': JSON.stringify(props),
  })

  const findChildByType = (parent: HTMLElement | ChildNode, type: string): Element | null => {
    const children = (parent as HTMLElement).childNodes
    for (let i = 0; i < children.length; i++) {
      if ((children[i] as Element).tagName === type) return children[i] as Element
    }
    return null
  }

  const findChildByDataSkeletonLoad = (parent: HTMLElement | ChildNode): Element | null => {
    const children = (parent as HTMLElement).childNodes
    for (let i = 0; i < children.length; i++) {
      if ((children[i] as Element).getAttribute?.('data-skeleton-load'))
        return children[i] as Element
    }
    return null
  }

  const calculateDimensions = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref || !ref.firstChild) return

      // Use requestAnimationFrame to defer calculations and prevent forced reflows
      requestAnimationFrame(() => {
        const child = findTag
          ? ((findChildByType(ref, findTag) ||
              findChildByType(ref.firstChild as Element, findTag)) as Element)
          : findChildByDataSkeletonLoad(ref) ||
            findChildByDataSkeletonLoad(ref.firstChild as Element) ||
            (ref.firstChild as Element)

        if (!child) return

        const childClass = child.getAttribute('class') || ''
        const data = child.getAttribute('data-skeleton-load')
        const { minWidth, minHeight } = data ? JSON.parse(data) : { minWidth: 0, minHeight: 0 }

        // Batch DOM queries to minimize reflows
        const rect = child.getBoundingClientRect()
        const style = getComputedStyle(child)

        const cWidth = rect.width < minWidth ? minWidth : rect.width
        const cHeight = rect.height < minHeight ? minHeight : rect.height

        setDimensions({
          width: cWidth,
          height: cHeight,
          borderRadius: style.borderRadius,
          className: childClass,
        })
      })
    },
    [findTag],
  )

  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const ref = containerRef
    if (ref) {
      // Debounce resize calculations to prevent excessive reflows
      const debouncedCalculate = () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        timeoutId = setTimeout(() => calculateDimensions(ref), 16) // ~60fps
      }

      resizeObserver = new ResizeObserver(debouncedCalculate)
      if (ref.firstChild) resizeObserver.observe(ref.firstChild as Element)
      if (!isLoading) resizeObserver?.disconnect()
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      resizeObserver?.disconnect()
    }
  }, [isLoading, findTag, containerRef, calculateDimensions])

  const skeletonStyle = cssVars({
    '--haus-skeletonLoader__skeleton__width': `${dimensions.width}px`,
    '--haus-skeletonLoader__skeleton__height': `${dimensions.height}px`,
    '--haus-skeletonLoader__skeleton__border-radius': dimensions.borderRadius,
  })

  return (
    <>
      {isLoading && (
        <div
          style={skeletonStyle}
          className={classNames(
            styles.skeleton,
            dimensions.className,
            hidden && styles.skeletonHidden,
          )}
        />
      )}
      <div
        ref={(el) => setContainerRef(el)}
        className={classNames(styles.container, isLoading && styles.containerLoading)}
      >
        {renderChildren(children, { skeletonAttributes })}
      </div>
    </>
  )
}
